import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Clock, Share2, Bookmark, ArrowLeft, ChevronRight, TrendingUp, TrendingDown, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getDossiers, getDossierById } from '../services/api';
import ArticleChat from '../components/ArticleChat';
import { formatDateEST, formatDateESTShort } from '../utils/dateUtils';

const SERIF_FONT = "'Georgia', 'Times New Roman', serif";

const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=800',
  'https://images.unsplash.com/photo-1495020689067-958852a7765e?w=800',
  'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=800',
];

const getImageUrl = (item, index = 0) => {
  if (item?.cover_image) {
    if (item.cover_image.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${item.cover_image}`;
    }
    return item.cover_image;
  }
  return PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length];
};

// ============================================
// DOSSIERS LIST PAGE - Forbes Style
// ============================================
export const DossiersListPage = () => {
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch all active dossiers with short timeout
        const data = await getDossiers({ status: 'active' });
        console.log('✅ Dossiers cargados:', data?.length || 0);
        
        if (Array.isArray(data) && data.length > 0) {
          setDossiers(data);
        } else {
          console.warn('⚠️ No se encontraron dossiers activos');
          setDossiers([]);
        }
      } catch (error) {
        console.error('❌ Error cargando dossiers:', error);
        setDossiers([]); // Asegurar que sea array vacío en caso de error
      } finally {
        setLoading(false);
      }
    };
    
    // Start fetching immediately
    fetchData();
  }, []);

  const filters = ['all', 'política', 'economía', 'negocios', 'actualidad'];
  
  // Filter with case-insensitive comparison and normalize sector names
  const filteredDossiers = filter === 'all' 
    ? dossiers 
    : dossiers.filter(d => {
        const sector = d.sector?.toLowerCase().trim() || '';
        return sector === filter || sector === filter.replace('í', 'i'); // Handle accents
      });

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Page Header */}
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: SERIF_FONT }}>
            Noticias
          </h1>
          <p className="text-gray-600 mt-2">Las últimas noticias de política, economía y negocios</p>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-6 py-4 overflow-x-auto">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-sm font-medium whitespace-nowrap pb-2 border-b-2 transition-colors ${
                  filter === f
                    ? 'text-gray-900 border-red-600'
                    : 'text-gray-500 border-transparent hover:text-gray-900'
                }`}
              >
                {f === 'all' ? 'Todas' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Featured Article */}
        {filteredDossiers[0] && (
          <Link to={`/dossiers/${filteredDossiers[0].dossier_id}`} className="block group mb-12">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                <img
                  src={getImageUrl(filteredDossiers[0], 0)}
                  alt={filteredDossiers[0].title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="flex flex-col justify-center">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  {filteredDossiers[0].sector || 'DESTACADO'}
                  {filteredDossiers[0].primary_country && filteredDossiers[0].primary_country !== 'Global' && (
                    <span className="ml-2 text-red-600">• {filteredDossiers[0].primary_country}</span>
                  )}
                </p>
                <h2
                  className="text-3xl font-bold text-gray-900 group-hover:text-red-600 transition-colors mb-4"
                  style={{ fontFamily: SERIF_FONT }}
                >
                  {filteredDossiers[0].title}
                </h2>
                <p className="text-gray-600 line-clamp-3 mb-4">
                  {filteredDossiers[0].what_changed_today || filteredDossiers[0].executive_summary?.[0]}
                </p>
                <p className="text-sm text-gray-500">
                  Por <span className="text-gray-900">{filteredDossiers[0].author || 'Equipo Editorial'}</span>
                </p>
              </div>
            </div>
          </Link>
        )}

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredDossiers.slice(1).map((dossier, i) => (
            <Link
              key={dossier.dossier_id}
              to={`/dossiers/${dossier.dossier_id}`}
              className="block group"
            >
              <div className="aspect-[16/10] overflow-hidden bg-gray-100 mb-4">
                <img
                  src={getImageUrl(dossier, i + 1)}
                  alt={dossier.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                {dossier.sector}
                {dossier.primary_country && dossier.primary_country !== 'Global' && (
                  <span className="ml-2 text-red-600">• {dossier.primary_country}</span>
                )}
              </p>
              <h3
                className="text-lg font-bold text-gray-900 group-hover:text-red-600 transition-colors line-clamp-2 mb-2"
                style={{ fontFamily: SERIF_FONT }}
              >
                {dossier.title}
              </h3>
              <p className="text-sm text-gray-500">
                Por {dossier.author || 'Equipo Editorial'}
              </p>
            </Link>
          ))}
        </div>

        {filteredDossiers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No hay noticias en esta categoría
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// DOSSIER DETAIL PAGE - Forbes Style
// ============================================
export const DossierDetailPage = () => {
  const { dossierId } = useParams();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDossier = async () => {
      try {
        const data = await getDossierById(dossierId);
        setDossier(data);
        // Track view for popularity algorithm
        import('../services/api').then(api => api.trackDossierView(dossierId));
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDossier();
  }, [dossierId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Cargando...</div>
      </div>
    );
  }

  if (!dossier) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-500">Noticia no encontrada</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/dossiers" className="flex items-center gap-2 text-sm text-gray-500 hover:text-red-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Volver a Noticias
          </Link>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 py-8">
        {/* Cover Image */}
        {dossier.cover_image && (
          <div className="aspect-video mb-8 rounded-lg overflow-hidden bg-gray-100">
            <img 
              src={dossier.cover_image} 
              alt={dossier.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {/* Category & Country */}
        <p className="text-xs font-bold text-red-600 uppercase tracking-wider mb-4">
          {dossier.sector || 'NOTICIAS'}
          {dossier.primary_country && dossier.primary_country !== 'Global' && (
            <span className="ml-2 text-gray-500">• {dossier.primary_country}</span>
          )}
          {dossier.countries && dossier.countries.length > 1 && (
            <span className="ml-1 text-gray-400 text-[10px]">
              (+{dossier.countries.length - 1})
            </span>
          )}
        </p>

        {/* Title */}
        <h1
          className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6"
          style={{ fontFamily: SERIF_FONT }}
        >
          {dossier.title}
        </h1>

        {/* Summary */}
        <p className="text-xl text-gray-600 leading-relaxed mb-6">
          {dossier.what_changed_today || dossier.executive_summary?.[0]}
        </p>

        {/* Meta */}
        <div className="flex items-center justify-between py-4 border-y border-gray-200 mb-8">
          <div className="flex items-center gap-4">
            <p className="text-sm">
              Por <span className="font-bold text-gray-900">{dossier.author || 'Equipo Editorial'}</span>
            </p>
            <span className="text-gray-300">|</span>
            <p className="text-sm text-gray-500 flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {formatDateEST(dossier.published_at || dossier.created_at)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm">
              <Share2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm">
              <Bookmark className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          {dossier.content ? (
            <div 
              className="text-gray-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ 
                __html: dossier.content.includes('<p>') 
                  ? dossier.content 
                  : dossier.content.split('\n\n').map(p => `<p>${p.trim()}</p>`).join('')
              }}
              style={{
                '--paragraph-spacing': '1.5rem'
              }}
            />
          ) : (
            dossier.executive_summary?.map((point, i) => (
              <p key={i} className="text-gray-800 leading-relaxed mb-6">{point}</p>
            ))
          )}
        </div>

        {/* Key Points */}
        {dossier.key_points?.length > 0 && (
          <div className="mt-12 p-6 bg-gray-50 rounded-lg border-l-4 border-red-600">
            <h3 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: SERIF_FONT }}>
              Puntos Clave
            </h3>
            <ul className="space-y-3">
              {dossier.key_points.map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-700">
                  <span className="text-red-600 font-bold mt-0.5">•</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Analysis: Who Benefits / Who Loses */}
        {(dossier.who_benefits || dossier.who_loses) && (
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {dossier.who_benefits && (
              <div className="p-6 bg-green-50 rounded-lg border border-green-100">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <h3 className="font-bold text-green-800">Quién se Beneficia</h3>
                </div>
                <p className="text-green-900">{dossier.who_benefits}</p>
              </div>
            )}
            {dossier.who_loses && (
              <div className="p-6 bg-red-50 rounded-lg border border-red-100">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                  <h3 className="font-bold text-red-800">Quién se Perjudica</h3>
                </div>
                <p className="text-red-900">{dossier.who_loses}</p>
              </div>
            )}
          </div>
        )}

        {/* Editorial Take */}
        {dossier.editorial_take && (
          <div className="mt-12 p-6 bg-gray-900 text-white rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-5 h-5 text-yellow-400" />
              <h3 className="font-bold text-yellow-400">Nuestra Perspectiva</h3>
            </div>
            <p className="text-gray-200 leading-relaxed italic">"{dossier.editorial_take}"</p>
          </div>
        )}

        {/* Signals to Watch */}
        {dossier.signals_to_watch?.length > 0 && (
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: SERIF_FONT }}>
              Señales a Observar
            </h3>
            <ul className="space-y-2">
              {dossier.signals_to_watch.map((signal, i) => (
                <li key={i} className="flex items-start gap-2 text-gray-700">
                  <span className="text-red-600 mt-1">•</span>
                  {signal}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Impact */}
        {dossier.sector_impacts?.length > 0 && (
          <div className="mt-12">
            <h3 className="text-lg font-bold text-gray-900 mb-6" style={{ fontFamily: SERIF_FONT }}>
              Impacto por Sector
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {dossier.sector_impacts.map((impact, i) => (
                <div key={i} className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="font-bold text-gray-900 mb-2">{impact.sector}</h4>
                  <p className="text-sm text-gray-600">{impact.impact}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Source */}
        {dossier.source && (
          <div className="mt-12 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Fuente: <span className="text-gray-700">{dossier.source}</span>
            </p>
          </div>
        )}
      </article>

      {/* Article Chat - Contextual AI */}
      <ArticleChat 
        dossierId={dossier.dossier_id} 
        articleTitle={dossier.title}
      />
    </div>
  );
};

export default DossiersListPage;
